import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { toast } from 'react-toastify'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import FaqBulkBar from '../components/faq/FaqBulkBar'
import FaqDownloadButton from '../components/faq/FaqDownloadButton'
import FaqEditPanel from '../components/faq/FaqEditPanel'
import FaqFilters from '../components/faq/FaqFilters'
import FaqGroupsDialog from '../components/faq/FaqGroupsDialog'
import FaqImportDialog from '../components/faq/FaqImportDialog'
import FaqLegacyImportDialog from '../components/faq/FaqLegacyImportDialog'
import FaqList from '../components/faq/FaqList'
import FaqSyncChip from '../components/faq/FaqSyncChip'
import {
  NO_FILTERS, PAGE_SIZE, SEARCH_DEBOUNCE_MS, UNGROUPED, exportFallbackName, sourceOptions,
} from '../components/faq/faqOptions'
import useFaqCapabilities from '../components/faq/useFaqCapabilities'
import FaqSandboxNotice from '../components/faq/FaqSandboxNotice'
import BotSandboxPanel from '../components/botconfig/BotSandboxPanel'
import useFaqStatus from '../components/faq/useFaqStatus'
import {
  RETRYABLE_FAQ_ERRORS,
  bulkDeleteFaq,
  bulkSetFaqGroup,
  deleteFaqEntry,
  exportFaq,
  faqErrorCode,
  faqErrorMessage,
  getFaqEntry,
  listFaqEntries,
  listFaqGroups,
  listFaqLegacyDocuments,
  retryFaqSync,
} from '../api/faq'
import { useLangPrefix } from '../hooks/useLangPrefix'
import useManagerStore from '../store/managerStore'

function Notice({ tone = 'slate', children }) {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-500',
    blue: 'border-blue-200 bg-blue-50 text-blue-800',
    red: 'border-red-200 bg-red-50 text-red-700',
  }
  return <div className={`rounded-lg border p-4 text-sm ${tones[tone]}`}>{children}</div>
}

function ErrorNotice({ message, error, onRetry }) {
  const { t } = useTranslation()
  // A failed lookup offers to try again; it is never shown as "no permission".
  const retryable = RETRYABLE_FAQ_ERRORS.has(faqErrorCode(error))
  return (
    <div role="status" aria-live="polite">
      <Notice tone="red">
        <span>{message || faqErrorMessage(t, error)}</span>
        {retryable && (
          <button type="button" onClick={onRetry} className="ml-3 font-semibold underline underline-offset-2">
            {t('common.retry')}
          </button>
        )}
      </Notice>
    </div>
  )
}

const FIRST_PAGE = { ...NO_FILTERS, page: 1 }
// `panel`: the entry open in the edit panel, `{ entry, prefill? }` (entry null = a new one), or null.
const freshView = (tenantId) => ({
  tenantId, searchText: '', applied: FIRST_PAGE, selection: new Set(), panel: null, groupsOpen: false, importOpen: false, legacyOpen: false,
})
const NO_LIST = { key: null, items: [], total: 0, error: null }
const secondaryButton = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700'

// What one list request asks for; also the identity of its answer.
const requestKey = (tenantId, applied, refresh) => JSON.stringify([tenantId, applied, refresh])

export default function FaqPage() {
  const { t, i18n } = useTranslation()
  const tenantId = useManagerStore((s) => s.tenantId)
  const { prefix } = useLangPrefix()
  const { capabilities, loading: capabilitiesLoading, error: capabilitiesError, refetch } = useFaqCapabilities(tenantId)
  const { status, error: statusError, notifyWrite } = useFaqStatus(tenantId)

  // Per-tenant page state: switching the branch starts from a clean page, with
  // no filter, selection, open entry or dialog of the previous branch.
  const [view, setView] = useState(() => freshView(tenantId))
  if (view.tenantId !== tenantId) setView(freshView(tenantId))
  const current = view.tenantId === tenantId ? view : freshView(tenantId)
  const { searchText, applied, selection, panel, groupsOpen, importOpen, legacyOpen } = current
  // Applied to the state as it is when it runs, so a change made while a request was in flight is kept.
  const update = useCallback((changes) => {
    setView((previous) => (previous.tenantId === tenantId ? { ...previous, ...changes(previous) } : previous))
  }, [tenantId])
  const setPanel = (next) => update(() => ({ panel: next }))
  const setGroupsOpen = (open) => update(() => ({ groupsOpen: open }))
  const setImportOpen = (open) => update(() => ({ importOpen: open }))
  const setLegacyOpen = (open) => update(() => ({ legacyOpen: open }))
  const panelDirty = useRef(false)
  const onPanelDirtyChange = useCallback((dirty) => {
    panelDirty.current = dirty
  }, [])

  const [groups, setGroups] = useState({ tenantId: null, items: [] })
  const [groupsVersion, setGroupsVersion] = useState(0)
  const [list, setList] = useState(NO_LIST)
  const [refresh, setRefresh] = useState(0)
  const [retrying, setRetrying] = useState(false)
  const [working, setWorking] = useState(false) // a bulk action or a row delete is running
  const [sandboxOpen, setSandboxOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024)

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const canRead = Boolean(tenantId && capabilities)
  // The counts changed (a sync finished, a retry was queued): the rows' sync dots are stale.
  const syncState = status ? `${status.synced}:${status.pending}:${status.syncing}:${status.failed}` : ''
  const key = canRead ? requestKey(tenantId, applied, `${refresh}:${syncState}`) : null

  // The search text is applied after a pause, together with the jump to page 1.
  useEffect(() => {
    const text = searchText.trim()
    if (text === applied.q) return undefined
    const timer = setTimeout(() => {
      setView((previous) => (
        previous.tenantId === tenantId
          ? { ...previous, applied: { ...previous.applied, q: text, page: 1 }, selection: new Set() }
          : previous
      ))
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchText, applied.q, tenantId])

  useEffect(() => {
    if (!key) return undefined
    let alive = true
    const { page, ...filters } = applied
    listFaqEntries(tenantId, { ...filters, page, page_size: PAGE_SIZE })
      .then((data) => {
        if (!alive) return
        const lastPage = Math.max(1, Math.ceil(data.total / PAGE_SIZE))
        if (page > lastPage) {
          // The list shrank under this page (entries were deleted elsewhere).
          setView((previous) => (
            previous.tenantId === tenantId ? { ...previous, applied: { ...previous.applied, page: lastPage } } : previous
          ))
          return
        }
        setList({ key, items: data.items, total: data.total, error: null })
      })
      .catch((error) => alive && setList({ key, items: [], total: 0, error }))
    return () => {
      alive = false
    }
    // `applied` is part of `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(() => {
    if (!canRead) return undefined
    let alive = true
    listFaqGroups(tenantId)
      .then((items) => {
        if (!alive) return
        setGroups({ tenantId, items })
        // A group that was deleted is no longer a filter.
        update((previous) => {
          const filter = previous.applied.group_id
          const gone = filter && filter !== UNGROUPED && !items.some((group) => group.id === filter)
          return gone ? { applied: { ...previous.applied, group_id: '', page: 1 } } : {}
        })
      })
      .catch((error) => alive && toast.error(faqErrorMessage(t, error)))
    return () => {
      alive = false
    }
  }, [canRead, tenantId, t, groupsVersion, update])

  // Old `faq` knowledge documents, for the overlap banner (any reader) and the
  // import (writers). `legacy_import` is absent until the backend ships the routes.
  const legacyListed = Boolean(
    canRead && capabilities.legacy_import !== undefined && capabilities.legacy_documents > 0,
  )
  const legacyEnabled = legacyListed && Boolean(capabilities.can_write && capabilities.legacy_import)
  const [legacy, setLegacy] = useState({ tenantId: null, items: [] })
  const [legacyVersion, setLegacyVersion] = useState(0)
  useEffect(() => {
    if (!legacyListed) return undefined
    let alive = true
    listFaqLegacyDocuments(tenantId)
      .then((items) => alive && setLegacy({ tenantId, items }))
      // The overlap banner is only a hint: if the list fails, the page works without it.
      .catch(() => alive && setLegacy({ tenantId, items: [] }))
    return () => {
      alive = false
    }
  }, [legacyListed, tenantId, legacyVersion])

  const reloadList = () => setRefresh((count) => count + 1)
  const reloadGroups = () => setGroupsVersion((count) => count + 1)

  // A write the server refused, or any other failure of an action on rows.
  const reportFailure = (error) => {
    toast.error(faqErrorMessage(t, error))
    // The capabilities were stale: the page no longer offers what the server refuses.
    if (faqErrorCode(error) === 'permission_denied') refetch()
  }

  // After any successful write: fresh rows, fresh counts, and the fast polling phase.
  // `closePanelFor`: ids whose open panel must close (they were deleted or changed elsewhere).
  const afterWrite = (message, closePanelFor = []) => {
    toast.success(message)
    update((previous) => ({
      selection: new Set(),
      panel: closePanelFor.includes(previous.panel?.entry?.id) ? null : previous.panel,
    }))
    reloadList()
    notifyWrite()
  }

  const runAction = async (action, message, ids = []) => {
    setWorking(true)
    try {
      afterWrite(message(await action()), ids)
    } catch (error) {
      reportFailure(error)
    } finally {
      setWorking(false)
    }
  }

  const formatCount = (value) => ({ count: value, formatted: Number(value).toLocaleString(i18n.language) })

  const deleteRow = (entry) => {
    if (!window.confirm(t('faq.panel.deleteConfirm', { question: entry.question }))) return
    runAction(() => deleteFaqEntry(tenantId, entry.id), () => t('faq.saved.deleted'), [entry.id])
  }

  // Opening an entry replaces the panel; unsaved changes in it are asked about first.
  const openEntry = (entry) => {
    if (panelDirty.current && !window.confirm(t('faq.panel.discardConfirm'))) return
    setPanel({ entry })
  }

  // "Thêm thủ công" of a legacy pair the FAQ rules refused: a new entry with its full text.
  const addManually = (prefill) => {
    if (panelDirty.current && !window.confirm(t('faq.panel.discardConfirm'))) return
    // A new form each time, even for the same pair again.
    update(() => ({ legacyOpen: false, panel: { entry: null, prefill, opened: Date.now() } }))
  }

  // The "open the existing question" link of a duplicate: the user chose to leave the form.
  const openExisting = async (entryId) => {
    try {
      const entry = await getFaqEntry(tenantId, entryId)
      setPanel({ entry })
    } catch (error) {
      reportFailure(error)
    }
  }

  const changeFilters = (next) => {
    const { q, ...selects } = next
    // The selects apply at once; the search text waits for the typing pause
    // (and only then moves to page 1), so typing sends a single request.
    update((previous) => {
      const selectChanged = Object.keys(selects).some((name) => selects[name] !== previous.applied[name])
      return selectChanged
        ? { searchText: q, applied: { ...previous.applied, ...selects, page: 1 }, selection: new Set() }
        : { searchText: q }
    })
  }
  const clearFilters = () => update(() => ({ searchText: '', applied: FIRST_PAGE, selection: new Set() }))
  const changePage = (page) => update((previous) => ({ applied: { ...previous.applied, page }, selection: new Set() }))

  const handleRetry = async () => {
    setRetrying(true)
    try {
      const result = await retryFaqSync(tenantId)
      toast.success(t('faq.chip.retryQueued', formatCount(result.requeued)))
      notifyWrite()
    } catch (error) {
      reportFailure(error)
    } finally {
      setRetrying(false)
    }
  }

  // Entries were created or changed (and groups maybe created): fresh rows,
  // groups and counts. An open, untouched panel may hold an overwritten version.
  const onImported = () => {
    update((previous) => ({ selection: new Set(), panel: panelDirty.current ? previous.panel : null }))
    reloadList()
    reloadGroups()
    notifyWrite()
  }

  const onLegacyImported = () => {
    onImported()
    setLegacyVersion((count) => count + 1) // imported counts feed the overlap banner
  }

  const canWrite = Boolean(capabilities?.can_write)
  const canImport = canWrite && Boolean(capabilities?.excel_import)
  const canExport = Boolean(capabilities?.excel_export)
  const botUsesFaq = Boolean(capabilities?.bot_uses_faq)
  const hasSandbox = Boolean(capabilities?.sandbox)
  const filtered = Boolean(applied.q || applied.group_id || applied.source || applied.sync_status)
  // Rows of an earlier request of THIS tenant stay visible while the next one loads;
  // rows of another tenant never do.
  const sameTenant = list.key !== null && JSON.parse(list.key)[0] === tenantId
  const shown = sameTenant ? list : NO_LIST
  const listLoading = key !== null && list.key !== key
  const number = (value) => Number(value).toLocaleString(i18n.language)
  const tenantGroups = groups.tenantId === tenantId ? groups.items : []
  const selectedIds = [...selection]
  // Documents already imported and still active: their pairs are answered twice.
  const overlapping = legacyListed && legacy.tenantId === tenantId
    ? legacy.items.filter((doc) => doc.imported_count > 0)
    : []

  let body
  if (!tenantId) {
    body = <Notice>{t('faq.needTenant')}</Notice>
  } else if (capabilitiesLoading && !capabilities) {
    body = <Notice>{t('common.loading')}</Notice>
  } else if (capabilitiesError) {
    body = <ErrorNotice error={capabilitiesError} onRetry={refetch} />
  } else {
    body = (
      <>
        {!botUsesFaq && <Notice tone="blue">{t('faq.notEnabledBanner')}</Notice>}
        {!canWrite && <Notice>{t('faq.viewOnly')}</Notice>}
        {overlapping.length > 0 && (
          <Notice tone="blue">
            <p>{t('faq.legacy.overlap')}</p>
            <ul className="mt-1 list-disc pl-5">
              {overlapping.map((doc) => <li key={doc.id} className="break-words">{doc.title}</li>)}
            </ul>
            <Link to={`${prefix}/bot-config`} className="mt-1 inline-block font-semibold underline underline-offset-2">
              {t('faq.legacy.overlapLink')}
            </Link>
          </Notice>
        )}
        {statusError && !status && (
          <ErrorNotice message={t('faq.statusError')} error={statusError} onRetry={notifyWrite} />
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-700">
            {status ? t('faq.tabs.faq', { count: number(status.total) }) : t('faq.tabs.faqNoCount')}
          </h2>
          {(canWrite || canExport) && (
            <div className="flex flex-wrap gap-2">
              {canWrite && (
                <button type="button" onClick={() => setGroupsOpen(true)} className={secondaryButton}>
                  {t('faq.manageGroups')}
                </button>
              )}
              {canImport && (
                <button type="button" onClick={() => setImportOpen(true)} className={secondaryButton}>
                  {t('faq.import.button')}
                </button>
              )}
              {legacyEnabled && (
                <button type="button" onClick={() => setLegacyOpen(true)} className={secondaryButton}>
                  {t('faq.legacy.button')}
                </button>
              )}
              {canExport && (
                <FaqDownloadButton
                  label={t('faq.export.button')}
                  // The rows of the current filter (not only the shown page).
                  fetchFile={() => {
                    const { page: _page, ...filters } = applied // every filter, not the page
                    return exportFaq(tenantId, filters)
                  }}
                  fallbackName={exportFallbackName}
                  onPermissionDenied={refetch}
                  className={`${secondaryButton} disabled:opacity-50`}
                />
              )}
              {canWrite && (
                <button
                  type="button"
                  onClick={() => openEntry(null)}
                  className="h-10 rounded-lg bg-[#1535c8] px-4 text-sm font-semibold text-white"
                >
                  {t('faq.addButton')}
                </button>
              )}
            </div>
          )}
        </div>
        <FaqFilters
          filters={{ ...applied, q: searchText }}
          onChange={changeFilters}
          groups={tenantGroups}
          sources={sourceOptions(status?.by_source, capabilities)}
        />
        {canWrite && (
          <FaqBulkBar
            count={selectedIds.length}
            groups={tenantGroups}
            busy={working}
            onClear={() => update(() => ({ selection: new Set() }))}
            onSetGroup={(groupId) => runAction(
              () => bulkSetFaqGroup(tenantId, selectedIds, groupId),
              (result) => t('faq.bulk.moved', formatCount(result.affected)),
              selectedIds, // an open panel of a moved entry holds a stale version
            )}
            onDelete={() => runAction(
              () => bulkDeleteFaq(tenantId, selectedIds),
              (result) => t('faq.bulk.deleted', formatCount(result.affected)),
              selectedIds,
            )}
          />
        )}
        {shown.error && !listLoading ? (
          <ErrorNotice error={shown.error} onRetry={reloadList} />
        ) : listLoading && shown.items.length === 0 ? (
          <Notice>{t('common.loading')}</Notice>
        ) : shown.total === 0 ? (
          <Notice>
            <span>{t(filtered ? 'faq.list.noResults' : 'faq.list.empty')}</span>
            {filtered && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-3 font-semibold text-[#1535c8] underline underline-offset-2"
              >
                {t('faq.list.clearFilters')}
              </button>
            )}
          </Notice>
        ) : (
          <FaqList
            items={shown.items}
            total={shown.total}
            page={applied.page}
            pageSize={PAGE_SIZE}
            maxEntries={status?.max_entries ?? capabilities.limits?.entries}
            statusTotal={status ? status.total : null}
            canWrite={canWrite}
            showUsage={Boolean(capabilities.usage_stats)}
            selection={selection}
            onSelectionChange={(next) => update(() => ({ selection: next }))}
            onPageChange={changePage}
            onOpen={openEntry}
            onDelete={deleteRow}
          />
        )}
      </>
    )
  }

  const editPanel = panel && capabilities && (
    <FaqEditPanel
      // A different entry, or the same one reloaded after a conflict, is a new form.
      key={`${panel.entry?.id ?? 'new'}:${panel.entry?.updated_at ?? ''}:${panel.opened ?? ''}`}
      tenantId={tenantId}
      entry={panel.entry}
      prefill={panel.prefill}
      groups={tenantGroups}
      limits={capabilities.limits}
      botUsesFaq={botUsesFaq}
      readOnly={!canWrite || panel.entry?.source === 'quick_answer'}
      isDesktop={isDesktop}
      onClose={() => setPanel(null)}
      onSaved={(_saved, { created }) => {
        setPanel(null)
        afterWrite(t(created ? 'faq.saved.created' : 'faq.saved.updated'))
      }}
      onDeleted={() => {
        setPanel(null)
        afterWrite(t('faq.saved.deleted'))
      }}
      onReloaded={(entry) => {
        setPanel({ entry })
        reloadList()
      }}
      onOpenExisting={openExisting}
      onGroupsChanged={reloadGroups}
      onPermissionDenied={refetch}
      onDirtyChange={onPanelDirtyChange}
    />
  )

  return (
    <ResponsiveShell title={t('faq.title')}>
      <div className={`mx-auto flex w-full ${hasSandbox ? 'max-w-7xl' : 'max-w-6xl'} flex-col gap-4 p-4 lg:p-6`}>
        <div className={hasSandbox ? 'flex flex-col gap-4 xl:grid xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start' : 'contents'}>
        {/* The title sits in the left column so the Thử bot column can start at the very top of the page. */}
        <div className="flex min-w-0 flex-col gap-4">
        <header className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('faq.breadcrumb')}</p>
            <h1 className="text-xl font-bold text-slate-900">{t('faq.title')}</h1>
            {capabilities && (
              <p className="mt-1 text-sm text-slate-500">{t(botUsesFaq ? 'faq.helper.bot' : 'faq.helper.neutral')}</p>
            )}
          </div>
          {capabilities && (
            <FaqSyncChip
              status={status}
              botUsesFaq={botUsesFaq}
              canWrite={canWrite}
              onRetry={handleRetry}
              retrying={retrying}
            />
          )}
        </header>
        {/* One tree for both widths: crossing 1024 px must not remount the panel and lose what was typed. */}
        <div className={editPanel && isDesktop ? 'flex items-start gap-4' : ''}>
          <div className="flex min-w-0 flex-1 flex-col gap-4">{body}</div>
          {editPanel}
        </div>
        </div>
        {hasSandbox && (
          <>
            {sandboxOpen && <button type="button" aria-label={t('faq.sandbox.close')} className="fixed inset-0 z-40 bg-black/30 xl:hidden" onClick={() => setSandboxOpen(false)} />}
            {/* xl+: a sticky column exactly as tall as the window (faq-sandbox-col). Below xl: a drawer opened from a floating button. One tree, so resizing never loses the chat. */}
            <div className={`${sandboxOpen ? 'fixed inset-y-0 right-0 z-50 flex w-full max-w-md bg-page-bg p-3' : 'hidden'} faq-sandbox-col min-w-0 flex-col gap-3 xl:sticky xl:z-auto xl:flex xl:max-w-none xl:bg-transparent xl:p-0`}>
              <button type="button" className="self-end rounded-lg px-2 py-1 text-xs text-text-muted hover:bg-primary-soft xl:hidden" onClick={() => setSandboxOpen(false)}>{t('faq.sandbox.close')}</button>
              <FaqSandboxNotice capabilities={capabilities} status={status} />
              <BotSandboxPanel tenantId={tenantId} enabled publishedOnly fill title={t('faq.sandbox.title')} hint={t('faq.sandbox.hint')} />
            </div>
            {!sandboxOpen && (
              <button type="button" className="fixed bottom-24 right-4 z-30 flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-primary-dark lg:bottom-6 xl:hidden" onClick={() => setSandboxOpen(true)}>
                <Sparkles size={16} aria-hidden="true" />{t('faq.sandbox.open')}
              </button>
            )}
          </>
        )}
        </div>
        {groupsOpen && canWrite && (
          <FaqGroupsDialog
            tenantId={tenantId}
            groups={tenantGroups}
            maxGroups={capabilities.limits?.groups ?? 20}
            onChanged={() => {
              reloadGroups()
              reloadList() // rows show their group's name
            }}
            onClose={() => setGroupsOpen(false)}
            onPermissionDenied={refetch}
          />
        )}
        {legacyOpen && legacyEnabled && (
          <FaqLegacyImportDialog
            tenantId={tenantId}
            onImported={onLegacyImported}
            onAddManually={addManually}
            onClose={() => setLegacyOpen(false)}
            onPermissionDenied={refetch}
          />
        )}
        {importOpen && canImport && (
          <FaqImportDialog
            tenantId={tenantId}
            showTemplate={canExport}
            onImported={onImported}
            onClose={() => setImportOpen(false)}
            onPermissionDenied={refetch}
          />
        )}
      </div>
    </ResponsiveShell>
  )
}
